import type { EtapaDoFunil } from "@base/contracts";
import { DESCRICAO_DA_ETAPA, ROTULO_DA_ETAPA } from "./funil";
import type { IdDoIndicador } from "./indicadores";

/**
 * Como cada numero do Dashboard e calculado, em portugues de gente. Escrito a
 * partir do calculo de verdade (API e dominio): se a regra mudar, muda aqui.
 */

export const EXPLICACAO_DO_INDICADOR: Readonly<Record<IdDoIndicador, string>> = {
  "faturamento-consulta":
    "Soma do campo Valor Consulta dos leads que, no período, passaram pela etapa mapeada como Consulta. Quem não passou nessa etapa não conta.",
  "faturamento-tratamento":
    "Soma do valor de venda do Kommo dos leads que, no período, passaram pela etapa mapeada como Tratamento. Sem essa etapa no mapeamento, usa as vendas ganhas. Quem não passou na etapa não conta.",
  investimento:
    "Quanto foi gasto em anúncios no período (Meta Ads + Google Ads). Depende da integração com as contas de anúncio: enquanto ela não existir, fica em branco em vez de mostrar um número inventado.",
  lucro:
    "Faturamento − Investimento. Sem investimento conhecido, fica em branco para não mostrar um lucro inventado.",
  "ticket-medio": "Faturamento ÷ número de vendas do período.",
  roas: "Faturamento ÷ Investimento: quantos reais de venda voltaram para cada R$ 1 investido em anúncios. Aparece quando o investimento vier do Meta Ads e do Google Ads.",
  "valor-em-aberto":
    "Soma do valor dos leads que estão agora na etapa de Consulta realizada (propostas ainda não fechadas). É a posição de hoje, não do fim do período, por isso não tem comparação.",
};

const COMO_CONTA: Readonly<Record<EtapaDoFunil, string>> = {
  gerenciador:
    "Leads gerados pelos anúncios no período (Meta Ads e Google Ads). Aparece quando as contas de anúncio forem conectadas.",
  crm: "Leads que entraram no CRM no período: passaram pela etapa escolhida como CRM em Conexões (ou por uma posterior) ou foram criados direto nela. Lead parado numa etapa anterior não conta.",
  mql: "Leads que, no período, entraram na etapa escolhida como MQL ou em qualquer etapa depois dela (histórico de etapas do Kommo). Quem pulou a etapa também conta.",
  agendado:
    "Leads que, no período, entraram na etapa escolhida como Agendado ou em qualquer etapa depois dela (histórico de etapas do Kommo).",
  consulta:
    "Leads que, no período, entraram na etapa escolhida como Consulta realizada ou na de Venda (histórico de etapas do Kommo).",
  venda:
    "Leads ganhos com data de fechamento no período, mais os que passaram pela etapa de venda e foram levados para outro funil por automação.",
};

const NOME_DO_CUSTO: Readonly<Record<Exclude<EtapaDoFunil, "gerenciador">, string>> = {
  crm: "Custo por lead (CPL)",
  mql: "Custo por MQL",
  agendado: "Custo por agendamento",
  consulta: "Custo por consulta",
  venda: "Custo por aquisição (CPA)",
};

/** Explicacao de uma etapa: como conta, a taxa ate a seguinte e o custo. */
export function explicacaoDaEtapa(etapa: EtapaDoFunil, seguinte: EtapaDoFunil | null): readonly string[] {
  const nome = ROTULO_DA_ETAPA[etapa];
  const linhas = [`${nome} (${DESCRICAO_DA_ETAPA[etapa].toLowerCase()}): ${COMO_CONTA[etapa]}`];
  if (seguinte) {
    linhas.push(`Taxa até ${ROTULO_DA_ETAPA[seguinte]}: ${ROTULO_DA_ETAPA[seguinte]} ÷ ${nome}.`);
  }
  if (etapa !== "gerenciador") {
    linhas.push(`${NOME_DO_CUSTO[etapa]}: Investimento ÷ ${nome}.`);
  }
  linhas.push("Meta: vem do Funil Projetado, proporcional aos dias do período.");
  return linhas;
}

export const LEGENDA_DO_FUNIL =
  "Em cada barra, o número claro é a meta do período (Funil Projetado) e o número forte é o realizado. Atrás de cada barra, a taxa de conversão para a etapa seguinte, no mesmo formato: meta | realizado. Sem Funil Projetado cadastrado, aparece só o realizado.";

export const EXPLICACAO_DO_PAINEL = {
  cpa: "Investimento ÷ vendas do período: quanto custou, em anúncios, cada paciente que fechou o tratamento.",
  global:
    "Vendas ÷ leads que entraram no CRM, os dois no mesmo período. Uma venda do período pode ser de um lead que chegou antes, então é a taxa do período, não de uma turma específica de leads.",
  ciclo:
    "Mediana dos dias entre a criação do lead no Kommo e a venda, contando só as vendas do período. Mediana: metade das vendas levou menos que isso e metade, mais.",
  ativos:
    "Leads em aberto agora no funil do CRM (nem ganhos nem perdidos). É a posição de hoje: não muda com o período escolhido.",
  resposta:
    "Mediana do tempo entre o primeiro contato do lead (a primeira mensagem dele no chat do Kommo, ou a criação do lead) e a primeira resposta de uma pessoa da equipe. Resposta enviada em menos de 1 minuto depois da mensagem do lead, sem usuário identificado no Kommo, é tratada como automática (robô) e não conta. Horário comercial: segunda a sexta, das 8h às 18h. Considera os leads que entraram no CRM no período.",
  rastreio:
    "Percentual dos leads que entraram no CRM no período com origem identificada: campo Origem preenchido (sem \"Fonte não identificada\") ou utm_source preenchida.",
  antigos:
    "Leads que entraram no CRM no período cujo contato principal no Kommo foi criado num dia anterior ao da oportunidade. Mesmo dia conta como lead novo. Só a data do contato entra — nome e telefone não.",
  utm: "Entre as oportunidades desta etapa no período, agrupa Campanha, Conjunto e Anúncio pelos campos escolhidos na conexão do Kommo. Sem escolha, usa utm_campaign, utm_content e utm_term. A ordem é quem vendeu mais em cada nível. A porcentagem é vendas ÷ oportunidades daquele recorte. Vale a lista inteira, não só a página da tabela.",
} as const;
